<template>
  <div v-editable="blok" class="storyblok-video">
    <div class="storyblok-video-media" :style="mediaStyle">
      <template v-if="embedUrl">
        <iframe
          :src="embedUrl"
          frameborder="0"
          allow="
            accelerometer;
            autoplay;
            clipboard-write;
            encrypted-media;
            gyroscope;
            picture-in-picture;
          "
          referrerpolicy="strict-origin-when-cross-origin"
          allowfullscreen
          class="storyblok-video-iframe"
        />
      </template>

      <template v-else-if="sourceUrl">
        <video controls class="storyblok-video-player" @loadedmetadata="onVideoMetadataLoaded">
          <source :src="sourceUrl" type="video/mp4" />
        </video>
      </template>

      <template v-else>
        <div v-if="!sourceUrl" class="storyblok-video-placeholder" />
        <button
          v-if="sourceUrl"
          type="button"
          class="storyblok-video-play-button"
          aria-label="Play video"
        >
          <span>&#9658;</span>
        </button>
      </template>
    </div>

    <div class="storyblok-video-content">
      <h3 v-if="blok.title" class="storyblok-video-title">{{ blok.title }}</h3>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { StoryblokVideoBlok } from '@/features/storyblok/types';

const props = defineProps<{
  blok: StoryblokVideoBlok;
}>();

const sourceAspectRatio = ref<string | null>(null);
const isAutoplay = computed(() => props.blok.autoplay ?? false);

const sourceUrl = computed(() => props.blok.file?.cached_url?.trim() ?? '');

const normalizeAspectRatioInput = (rawRatio: string): string | null => {
  if (!rawRatio?.trim()) {
    return null;
  }

  const normalized = rawRatio.trim().replace(/\s+/g, '');
  const ratioMatch = normalized.match(/^(\d+(?:\.\d+)?)[/:](\d+(?:\.\d+)?)$/);

  if (ratioMatch) {
    const width = Number(ratioMatch[1]);
    const height = Number(ratioMatch[2]);

    if (width > 0 && height > 0) {
      return `${width} / ${height}`;
    }

    return null;
  }

  const decimal = Number(normalized);
  if (Number.isFinite(decimal) && decimal > 0) {
    return `${decimal} / 1`;
  }

  return null;
};

const manualAspectRatio = computed(() => {
  const configuredRatio = props.blok.aspectRatio ?? props.blok.ratio ?? '';
  return normalizeAspectRatioInput(configuredRatio);
});

const getEmbedAspectRatio = (rawUrl: string): string | null => {
  if (!rawUrl) {
    return null;
  }

  try {
    const parsed = new URL(rawUrl.trim());
    const host = parsed.hostname.toLowerCase();
    const path = parsed.pathname;

    if (
      (host.includes('youtube.com') || host.includes('youtu.be')) &&
      path.startsWith('/shorts/')
    ) {
      return '9 / 16';
    }
  } catch {
    if (rawUrl.includes('youtube.com/shorts/')) {
      return '9 / 16';
    }
  }

  return null;
};

const onVideoMetadataLoaded = (event: Event): void => {
  const videoElement = event.currentTarget as HTMLVideoElement | null;
  if (!videoElement || !videoElement.videoWidth || !videoElement.videoHeight) {
    return;
  }

  sourceAspectRatio.value = `${videoElement.videoWidth} / ${videoElement.videoHeight}`;
};

const normalizeEmbedUrl = (rawUrl: string, autoplayEnabled: boolean): string | null => {
  if (!rawUrl) {
    return null;
  }

  const autoplayQuery = autoplayEnabled ? '?autoplay=1&mute=1&playsinline=1' : '';

  try {
    const parsed = new URL(rawUrl.trim());
    const host = parsed.hostname.toLowerCase();
    const path = parsed.pathname;

    if (host.includes('youtube.com') || host.includes('youtu.be')) {
      let videoId = '';

      if (host.includes('youtu.be')) {
        videoId = path.split('/').filter(Boolean)[0] ?? '';
      } else if (path.startsWith('/watch')) {
        videoId = parsed.searchParams.get('v') ?? '';
      } else if (path.startsWith('/shorts/')) {
        videoId = path.split('/shorts/')[1]?.split('/')[0] ?? '';
      } else if (path.startsWith('/embed/')) {
        videoId = path.split('/embed/')[1]?.split('/')[0] ?? '';
      }

      return videoId ? `https://www.youtube.com/embed/${videoId}${autoplayQuery}` : null;
    }

    if (host.includes('vimeo.com')) {
      const parts = path.split('/').filter(Boolean);
      const videoId = parts[parts.length - 1] ?? '';
      return videoId ? `https://player.vimeo.com/video/${videoId}${autoplayQuery}` : null;
    }

    if (path.includes('/embed/')) {
      const embedUrl = new URL(rawUrl);
      if (autoplayEnabled) {
        embedUrl.searchParams.set('autoplay', '1');
        embedUrl.searchParams.set('mute', '1');
        embedUrl.searchParams.set('playsinline', '1');
      }
      return embedUrl.toString();
    }
  } catch {
    // Fallback for malformed URLs provided by CMS content.
    if (rawUrl.includes('youtube.com/shorts/')) {
      const videoId = rawUrl.split('youtube.com/shorts/')[1]?.split(/[?/&#]/)[0] ?? '';
      return videoId ? `https://www.youtube.com/embed/${videoId}${autoplayQuery}` : null;
    }

    if (rawUrl.includes('youtube.com/watch')) {
      const videoId = rawUrl.split('v=')[1]?.split('&')[0] ?? '';
      return videoId ? `https://www.youtube.com/embed/${videoId}${autoplayQuery}` : null;
    }

    if (rawUrl.includes('youtu.be/')) {
      const videoId = rawUrl.split('youtu.be/')[1]?.split(/[?/&#]/)[0] ?? '';
      return videoId ? `https://www.youtube.com/embed/${videoId}${autoplayQuery}` : null;
    }

    if (rawUrl.includes('vimeo.com/')) {
      const videoId = rawUrl.split('vimeo.com/')[1]?.split(/[?/&#]/)[0] ?? '';
      return videoId ? `https://player.vimeo.com/video/${videoId}${autoplayQuery}` : null;
    }

    if (rawUrl.includes('/embed/')) {
      if (!autoplayEnabled) {
        return rawUrl;
      }

      const separator = rawUrl.includes('?') ? '&' : '?';
      return `${rawUrl}${separator}autoplay=1&mute=1&playsinline=1`;
    }
  }

  return null;
};

const embedUrl = computed(() => normalizeEmbedUrl(sourceUrl.value, isAutoplay.value));
const mediaAspectRatio = computed(
  () =>
    manualAspectRatio.value ??
    getEmbedAspectRatio(sourceUrl.value) ??
    sourceAspectRatio.value ??
    '16 / 9'
);
const mediaStyle = computed(() => ({
  aspectRatio: mediaAspectRatio.value,
  maxHeight: '800px',
}));
// const showPlayer = computed(() => props.blok.file?.autoplay || isPlaying.value);

// const startPlayback = (): void => {
//   if (!sourceUrl.value || showPlayer.value) {
//     return;
//   }

//   isPlaying.value = true;
// };
</script>

<style scoped>
.storyblok-video {
  display: flex;
  flex-direction: column;
  gap: var(--ion-space-3);
  margin: var(--ion-space-3) 0;
}

.storyblok-video-media {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: var(--ion-radius-2xl);
  overflow: hidden;
  background: #111827;
}

.storyblok-video-image,
.storyblok-video-player,
.storyblok-video-iframe {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.storyblok-video-placeholder {
  width: 100%;
  height: 100%;
  background: #111827;
}

.storyblok-video-play-button {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 48px;
  height: 48px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  color: var(--ion-text-color-contrast);
  cursor: pointer;
}

.storyblok-video-content {
  display: flex;
  flex-direction: column;
  gap: var(--ion-space-2);
}

.storyblok-video-title,
.storyblok-video-description {
  margin: 0;
}

.storyblok-video-content :deep(.rich-text) {
  width: 100%;
  color: var(--ion-text-color);
}
</style>
